<script setup lang="ts">
/**
 * Renders Markdown as sanitized HTML with the kit's typography — chat
 * messages, agent answers, wiki pages, descriptions. GitHub flavour (tables,
 * task lists, strikethrough), single line breaks kept by default, links to
 * other origins open in a new tab.
 *
 * Module-specific syntax (wiki links, math, mermaid, syntax highlighting) comes
 * in through `extensions` — marked extensions or renderer overrides — and is
 * sanitized with the rest. `link-click` fires for every link click with the
 * href: call `event.preventDefault()` there to route in-app links through the
 * router instead of a page load.
 *
 * Mentions: `mentions` takes structured ranges into `source` (the comments'
 * `NxMention`); `textMentions` reads `@First Last` out of the text for bodies
 * that carry no ranges (chat). Either renders in the kit's mention look; with
 * `mentionsInteractive` each mention is a button and a click emits `mention`.
 * The HTML is sanitized as before.
 *
 * `underline` reads `++text++` as underlined text. `imageSource` lets the app
 * decide what each image shows: a source it loaded itself (an object URL, so
 * the request carries its credentials and the other site never sees the
 * reader), or no image at all, with the alt text as a link in its place.
 */
import { computed } from 'vue';
import type { NxMention } from '../Mentions/types';
import {
  createMarkdownParser,
  renderMarkdown,
  type NxMarkdownExtension,
  type NxMarkdownImageSource,
  type NxMarkdownMention,
  type NxSanitizeOptions,
} from './markdown';

const props = withDefaults(
  defineProps<{
    /** The Markdown text. */
    source: string;
    /** One line without paragraph wrapping; renders a `<span>`. */
    inline?: boolean;
    /** Single line breaks become `<br>`. */
    breaks?: boolean;
    /** Tighter paragraph and heading spacing, for chat bubbles and cards. */
    compact?: boolean;
    externalLinksInNewTab?: boolean;
    extensions?: readonly NxMarkdownExtension[];
    sanitize?: NxSanitizeOptions;
    /** People mentioned in `source`, as character ranges into it. */
    mentions?: readonly NxMention[];
    /** Read `@First Last` in the text as a mention (bodies without ranges). */
    textMentions?: boolean;
    /** Mentions are buttons that emit `mention`; otherwise plain text in the mention look. */
    mentionsInteractive?: boolean;
    /** `++text++` is underlined. */
    underline?: boolean;
    /** Maps an image's source to what the app loaded for it, or hides the image. */
    imageSource?: NxMarkdownImageSource;
  }>(),
  {
    inline: false,
    breaks: true,
    compact: false,
    externalLinksInNewTab: true,
    extensions: () => [],
    sanitize: () => ({}),
    mentions: () => [],
    textMentions: false,
    mentionsInteractive: false,
    underline: false,
    imageSource: undefined,
  }
);

const emit = defineEmits<{
  /** A link inside the text was clicked. `external` = another origin. */
  'link-click': [payload: { href: string; external: boolean; event: MouseEvent }];
  /** A mention was clicked (`mentionsInteractive`). */
  mention: [payload: NxMarkdownMention & { event: MouseEvent }];
}>();

const mentionOptions = computed(() => ({
  mentions: props.mentions,
  textMentions: props.textMentions,
  mentionsInteractive: props.mentionsInteractive,
}));

const parser = computed(() =>
  createMarkdownParser({
    breaks: props.breaks,
    extensions: props.extensions,
    underline: props.underline,
    ...mentionOptions.value,
  })
);

// Safe for v-html: renderMarkdown runs everything through DOMPurify.
const html = computed(() =>
  renderMarkdown(
    props.source,
    {
      inline: props.inline,
      externalLinksInNewTab: props.externalLinksInNewTab,
      sanitize: props.sanitize,
      imageSource: props.imageSource,
      ...mentionOptions.value,
    },
    parser.value
  )
);

function onClick(event: MouseEvent) {
  const mention = (event.target as Element | null)?.closest?.('[data-mention-name]');
  if (mention) {
    const account = mention.getAttribute('data-mention-account');
    emit('mention', {
      label: mention.textContent ?? '',
      name: mention.getAttribute('data-mention-name') ?? '',
      accountId: account === null ? null : Number(account),
      event,
    });
    return;
  }
  const anchor = (event.target as Element | null)?.closest?.('a[href]');
  if (!anchor) return;
  const href = anchor.getAttribute('href') ?? '';
  emit('link-click', { href, external: anchor.getAttribute('target') === '_blank', event });
}
</script>

<template>
  <span v-if="inline" class="nx-markdown is-inline" @click="onClick" v-html="html" />
  <div
    v-else
    class="nx-markdown"
    :class="{ 'is-compact': compact }"
    @click="onClick"
    v-html="html"
  />
</template>

<style scoped>
/*
 * Flat selectors on purpose: Vue's scoped compiler turns a `:deep()` nested
 * inside a CSS-nesting block into `.nx-markdown [data-v] p`, which never
 * matches the v-html content.
 */
.nx-markdown {
  --markdown-gap: var(--nxt-space-sm);

  min-width: 0;
  color: var(--nxt-fg);
  line-height: var(--nxt-line-height-normal);
  overflow-wrap: anywhere;
}
.nx-markdown.is-compact {
  --markdown-gap: var(--nxt-space-xs);
}
.nx-markdown > :deep(:first-child) {
  margin-top: 0;
}
.nx-markdown > :deep(:last-child) {
  margin-bottom: 0;
}
.nx-markdown :deep(:is(p, ul, ol, pre, table, blockquote, hr)) {
  margin: 0 0 var(--markdown-gap);
}
.nx-markdown :deep(:is(h1, h2, h3, h4, h5, h6)) {
  margin: calc(var(--markdown-gap) * 1.5) 0 var(--markdown-gap);
  color: var(--nxt-heading-fg);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
}
.nx-markdown :deep(h1) {
  font-size: var(--nxt-font-size-2xl);
}
.nx-markdown :deep(h2) {
  font-size: var(--nxt-font-size-xl);
}
.nx-markdown :deep(h3) {
  font-size: var(--nxt-font-size-lg);
}
.nx-markdown :deep(:is(h4, h5, h6)) {
  font-size: var(--nxt-font-size-md);
}
.nx-markdown.is-compact :deep(:is(h1, h2, h3)) {
  font-size: var(--nxt-font-size-lg);
}
.nx-markdown :deep(a) {
  color: var(--nxt-link-fg);
  text-decoration: underline;
  text-decoration-thickness: var(--nxt-hairline);
  text-underline-offset: 0.15em;
}
.nx-markdown :deep(a:hover) {
  color: var(--nxt-link-fg-hover);
}
.nx-markdown :deep(a:focus-visible) {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-sm);
}
.nx-markdown :deep(strong) {
  font-weight: var(--nxt-font-weight-semibold);
}
/* The theme resets list font sizes; lists follow the surrounding text. */
.nx-markdown :deep(:is(ul, ol)) {
  padding-left: var(--nxt-space-lg);
  font-size: inherit;
}
.nx-markdown :deep(ul) {
  list-style: disc;
}
.nx-markdown :deep(ol) {
  list-style: decimal;
}
.nx-markdown :deep(li) {
  margin: 0;
}
.nx-markdown :deep(li > :is(ul, ol)) {
  margin: 0;
}
.nx-markdown :deep(li:has(> input[type='checkbox'])) {
  list-style: none;
  margin-left: calc(var(--nxt-space-lg) * -1);
}
.nx-markdown :deep(li > input[type='checkbox']) {
  margin: 0 var(--nxt-space-xs) 0 0;
  vertical-align: middle;
  accent-color: var(--nxt-checkbox-bg-checked);
}
.nx-markdown :deep(code) {
  padding: 0.1em 0.35em;
  font-family: var(--nxt-font-mono);
  font-size: 0.9em;
  color: var(--nxt-code-fg);
  background: var(--nxt-code-bg);
  border-radius: var(--nxt-radius-sm);
}
.nx-markdown :deep(pre) {
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  overflow-x: auto;
  background: var(--nxt-code-bg);
  border: var(--nxt-hairline) solid var(--nxt-border-subtle);
  border-radius: var(--nxt-radius-md);
}
.nx-markdown :deep(pre code) {
  padding: 0;
  font-size: var(--nxt-font-size-sm);
  background: none;
  border-radius: 0;
}
.nx-markdown :deep(blockquote) {
  padding: var(--nxt-space-2xs) 0 var(--nxt-space-2xs) var(--nxt-space-md);
  color: var(--nxt-fg-muted);
  border-left: calc(var(--nxt-hairline) * 3) solid var(--nxt-border-strong);
}
.nx-markdown :deep(hr) {
  height: 0;
  border: 0;
  border-top: var(--nxt-hairline) solid var(--nxt-divider-color);
}
.nx-markdown :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: var(--nxt-font-size-sm);
}
.nx-markdown :deep(:is(th, td)) {
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  text-align: left;
  border: var(--nxt-hairline) solid var(--nxt-border);
}
.nx-markdown :deep(th) {
  font-weight: var(--nxt-font-weight-semibold);
  background: var(--nxt-surface-sunken-bg);
}
.nx-markdown :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: var(--nxt-radius-sm);
}
.nx-markdown :deep(del) {
  color: var(--nxt-fg-muted);
}
/* The kit's mention look, the same as in a comment (`.nx-comment-item__mention`). */
.nx-markdown :deep(.nx-markdown__mention) {
  display: inline;
  margin: 0;
  padding: 0 calc(var(--nxt-space-2xs) * 0.75);
  border: 0;
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
  font: inherit;
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-markdown :deep(button.nx-markdown__mention) {
  cursor: pointer;
}
.nx-markdown :deep(button.nx-markdown__mention:hover) {
  background: var(--nxt-brand-subtle-bg-hover);
}
.nx-markdown :deep(button.nx-markdown__mention:focus-visible) {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}
.nx-markdown.is-inline :deep(p) {
  display: inline;
  margin: 0;
}
</style>
